import { addMinutes, format, intlFormat } from 'date-fns'

import {
  selectDateFnsLocale,
  selectDateFnsLocaleString,
} from '~/helpers/selectDateFnsLocale'

import type { Language } from '~/types/language'

/**
 * 2 days in milliseconds.
 */
export const TWO_DAYS_MS = 172800000

/**
 * @param date - The date to format
 * @param locale - The app locale, most probably 'en' or 'fr'
 *
 * @returns The formatted date string
 *
 * @example
 * // Represent 4 October 2019 in German.
 * // Convert the date with format's options and locale's options.
 * const result = formatDateToDefaultDisplay(new Date(2019, 9, 4, 12, 30, 13, 456), 'de')
 * //=\> 4. Oktober 2019
 */
export function formatDateToDefaultDisplay(
  date: Date | string | number,
  locale: Language,
) {
  try {
    return intlFormat(
      new Date(date),
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      },
      {
        locale: selectDateFnsLocaleString(locale),
      },
    )
  } catch (error) {
    // Invalid date fallback for older Safari versions which fail to create the date
    return new Date(date).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
}

/**
 * .
 * This function converts a timestamp to UTC and return a formatted date string based on locale value.
 *
 * @param timestamp - A timestamp (e.g: 662601600000 -> equal to December 31 1990).
 * @param locale - User's locale retrieved by i18n.
 * @returns - Formatted date string based on locale value -> Dec 31, 1990 or 31 dec. 1990.
 */
export function formatTimestampToUTCString(
  timestamp: number,
  locale: Language,
): string | 'Invalid date' {
  // Convert timestamp to date object
  const date = new Date(timestamp)

  try {
    // Transform date to UTC date
    const utc = format(
      addMinutes(date, date.getTimezoneOffset()),
      'yyyy-MM-dd HH:mm:ss',
    )

    // Format UTC date using PP pattern from date-fns
    return format(new Date(utc), 'PP', {
      locale: selectDateFnsLocale(locale),
    })
  } catch (error) {
    return 'Invalid date'
  }
}
